import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";

const Users = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [pw, setPw] = useState({ currentPassword: "", newPassword: "", confirm: "" });
  const [pwLoading, setPwLoading] = useState(false);
  const [newUser, setNewUser] = useState({ name: "", email: "", password: "" });
  const [addLoading, setAddLoading] = useState(false);

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const changePassword = async (e) => {
    e.preventDefault();
    if (pw.newPassword.length < 6) { toast.error("Yeni şifre en az 6 karakter olmalı"); return; }
    if (pw.newPassword !== pw.confirm) { toast.error("Yeni şifreler eşleşmiyor"); return; }
    setPwLoading(true);
    try {
      await api.post("/auth/change-password", { currentPassword: pw.currentPassword, newPassword: pw.newPassword });
      toast.success("Şifreniz güncellendi");
      setPw({ currentPassword: "", newPassword: "", confirm: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Şifre değiştirilemedi");
    } finally { setPwLoading(false); }
  };

  const addUser = async (e) => {
    e.preventDefault();
    setAddLoading(true);
    try {
      await api.post("/users", newUser);
      toast.success("Yeni admin eklendi");
      setNewUser({ name: "", email: "", password: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eklenemedi");
    } finally { setAddLoading(false); }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`"${u.email}" kullanıcısı silinsin mi?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Silindi"); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Silinemedi");
    }
  };

  return (
    <div className="space-y-8" data-testid="users-page">
      <header>
        <h1 className="font-serif text-3xl text-zbeach-dark">Kullanıcılar & Güvenlik</h1>
        <p className="text-zbeach-muted text-sm mt-1">Kendi şifrenizi değiştirin veya ekip için yeni admin hesabı oluşturun.</p>
      </header>

      {/* Profile + Password Change */}
      <section className="bg-zbeach-surface border border-zbeach-gold/15 rounded-2xl p-5">
        <h2 className="font-serif text-xl text-zbeach-dark mb-1">Profilim</h2>
        <div className="flex items-center gap-3 mb-5 mt-2">
          <div className="w-12 h-12 rounded-full bg-zbeach-gold/20 flex items-center justify-center text-zbeach-gold font-semibold text-lg">{user?.name?.[0] || user?.email?.[0]?.toUpperCase() || "A"}</div>
          <div>
            <div className="font-medium text-zbeach-dark">{user?.name || "Admin"}</div>
            <div className="text-xs text-zbeach-muted">{user?.email}</div>
          </div>
        </div>

        <form onSubmit={changePassword} className="grid grid-cols-1 md:grid-cols-3 gap-3 max-w-3xl" data-testid="change-password-form">
          <PwField label="Mevcut Şifre" value={pw.currentPassword} onChange={(v) => setPw({ ...pw, currentPassword: v })} testid="cp-current" />
          <PwField label="Yeni Şifre" value={pw.newPassword} onChange={(v) => setPw({ ...pw, newPassword: v })} testid="cp-new" />
          <PwField label="Yeni Şifre (tekrar)" value={pw.confirm} onChange={(v) => setPw({ ...pw, confirm: v })} testid="cp-confirm" />
          <div className="md:col-span-3 flex justify-end">
            <button type="submit" disabled={pwLoading} className="px-5 py-2 rounded-full bg-zbeach-turquoise text-white text-sm font-medium btn-glow disabled:opacity-50" data-testid="cp-submit">
              {pwLoading ? "Güncelleniyor..." : "Şifreyi Değiştir"}
            </button>
          </div>
        </form>
      </section>

      {/* Admin user list */}
      <section className="bg-zbeach-surface border border-zbeach-gold/15 rounded-2xl p-5">
        <h2 className="font-serif text-xl text-zbeach-dark mb-3">Admin Kullanıcılar</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[500px]">
            <thead className="bg-zbeach-bg text-zbeach-muted uppercase text-xs tracking-wider">
              <tr><th className="text-left p-3">Ad</th><th className="text-left p-3">E-posta</th><th className="text-left p-3">Eklenme</th><th className="text-right p-3">İşlem</th></tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-t border-zbeach-gold/10" data-testid={`user-row-${u.id}`}>
                  <td className="p-3 font-medium">{u.name} {u.id === user?.id && <span className="text-[10px] text-zbeach-gold ml-1">(siz)</span>}</td>
                  <td className="p-3 text-zbeach-muted">{u.email}</td>
                  <td className="p-3 text-xs text-zbeach-muted">{u.createdAt ? new Date(u.createdAt).toLocaleDateString("tr-TR") : "—"}</td>
                  <td className="p-3 text-right">
                    {u.id !== user?.id && (
                      <button onClick={() => removeUser(u)} className="text-red-600 hover:underline text-xs" data-testid={`user-delete-${u.id}`}>Sil</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Add new admin */}
      <section className="bg-zbeach-surface border border-zbeach-gold/15 rounded-2xl p-5">
        <h2 className="font-serif text-xl text-zbeach-dark mb-1">Yeni Admin Ekle</h2>
        <p className="text-xs text-zbeach-muted mb-4">Ekip arkadaşlarınız için yeni admin hesabı oluşturun.</p>
        <form onSubmit={addUser} className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="add-user-form">
          <div>
            <label className="block text-xs uppercase tracking-wider text-zbeach-muted mb-1">Ad</label>
            <input value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} placeholder="Ad Soyad" className="w-full px-3 py-2 rounded-lg bg-zbeach-bg border border-zbeach-gold/20 text-sm" data-testid="new-user-name" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-zbeach-muted mb-1">E-posta *</label>
            <input type="email" required value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} placeholder="ornek@zbeach.com" className="w-full px-3 py-2 rounded-lg bg-zbeach-bg border border-zbeach-gold/20 text-sm" data-testid="new-user-email" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-zbeach-muted mb-1">Şifre * (en az 6)</label>
            <input type="password" required value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} placeholder="••••••" minLength={6} className="w-full px-3 py-2 rounded-lg bg-zbeach-bg border border-zbeach-gold/20 text-sm" data-testid="new-user-password" />
          </div>
          <div className="md:col-span-3 flex justify-end">
            <button type="submit" disabled={addLoading} className="px-5 py-2 rounded-full bg-zbeach-turquoise text-white text-sm font-medium btn-glow disabled:opacity-50" data-testid="add-user-submit">
              {addLoading ? "Ekleniyor..." : <><i className="fa-solid fa-user-plus mr-1" /> Admin Ekle</>}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
};

const PwField = ({ label, value, onChange, testid }) => (
  <div>
    <label className="block text-xs uppercase tracking-wider text-zbeach-muted mb-1">{label}</label>
    <input type="password" required value={value} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2 rounded-lg bg-zbeach-bg border border-zbeach-gold/20 text-sm" data-testid={testid} />
  </div>
);

export default Users;
